<template>
  <!-- PREMIUM LOADING OVERLAY -->
  <div v-if="isLoading" class="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden">
    <!-- Blurred Backdrop -->
    <div class="absolute inset-0 bg-[#0f1418]/60 backdrop-blur-md"></div>
    
    <!-- Content -->
    <div class="relative flex flex-col items-center gap-8 px-6 text-center">
      <!-- Animated Spinner/Ring -->
      <div class="relative flex h-24 w-24 items-center justify-center">
        <div class="absolute h-full w-full animate-spin rounded-full border-4 border-white/10 border-t-emerald-500"></div>
        <font-awesome-icon icon="fa-solid fa-shield-halved" class="text-3xl text-emerald-400/80" />
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-2xl font-bold tracking-tight text-white md:text-3xl">
          {{ isPaying ? 'Verifying Payment' : 'Processing Request' }}
        </h2>
        <p class="max-w-md text-base text-slate-300/90 leading-relaxed">
          {{ isPaying ? 'We are securely communicating with your bank to confirm the transaction.' : 'We are finalizing your request. Please wait...' }}
          <span class="block mt-1 font-semibold text-emerald-400/90">Please do not refresh or close this page.</span>
        </p>
      </div>

      <!-- Progress bar simulation -->
      <div class="h-1.5 w-64 overflow-hidden rounded-full bg-white/10">
        <div class="h-full animate-progress rounded-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]"></div>
      </div>
    </div>
  </div>

  <!-- ERROR STATE -->
  <div v-else-if="hasError" class="flex flex-col gap-3 items-center justify-center w-full h-full">
    <h3 class="text-theme-gray-5 text-head-md-2 md:text-head-xl text-center flex flex-col gap-3 mb-3">
      <div>Error</div>
    </h3>
    <div class="rounded-md bg-red-50 p-4 w-full">
      <div class="flex flex-col items-start gap-2 md:flex-row md:items-center">
        <div class="flex items-start gap-2">
          <font-awesome-icon size="lg" icon="fa-solid fa-times-circle" class="text-red-400" />
          <div class="flex flex-col md:flex-row items-start gap-1">
            <p class="text-sm font-medium text-red-700">{{ errorMessage }}</p>
            <div
              @click="handleGoBack"
              class="font-medium text-sm text-red-700 underline hover:text-red-600 cursor-pointer"
            >
              {{ isAgencyOrCorporate ? 'Go to dashboard' : 'Go to home' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- SUCCESS / RESULT -->
  <div v-else class="grid grid-cols-12 md:gap-10">
    <!-- SUCCESS -->
    <div v-if="feedType === 'success'" class="col-span-12 md:pb-12 flex flex-col items-center justify-center gap-8">
      <!-- Icon -->
      <div class="relative">
        <div class="absolute -left-4 flex h-[120px] w-[120px] items-center justify-center rounded-full border border-theme-gray-7">
          <font-awesome-icon class="text-theme-active text-4xl" icon="fa-solid fa-check" />
        </div>
        <div class="h-[120px] w-[120px] rounded-full bg-theme-gray-1"></div>
      </div>

      <!-- Title + Booking number badge -->
      <div class="flex flex-col items-center gap-3">
        <h3 class="text-center text-theme-gray-5 text-4xl md:text-6xl font-semibold">
          Thank You
        </h3>
        <div v-if="bookingNumber" class="rounded-full border border-slate-300/70 bg-white px-4 py-1 text-sm font-semibold text-slate-600">
          Booking #{{ bookingNumber }}
        </div>
      </div>

      <!-- Success message card -->
      <div class="w-full">
        <div class="mx-auto max-w-4xl rounded-2xl border border-green-200 bg-green-50 px-6 py-6 text-center">
          <p class="text-lg md:text-2xl font-semibold leading-relaxed text-green-800">
            {{ message }}
          </p>
          <p class="mt-2 text-sm md:text-base text-green-700/80">
            A confirmation email (and account details if requested) has been sent to you. 
            <span class="font-bold">If you don't see it in your inbox shortly, please check your spam folder.</span>
          </p>

          <!-- CTA -->
          <button
            @click="handleGoBack"
            class="mt-5 inline-flex items-center justify-center rounded-full border border-green-700 px-6 py-2.5 text-base md:text-lg font-semibold text-green-800 hover:bg-white"
          >
            {{ isAgencyOrCorporate ? 'Go to dashboard' : 'Go to home' }}
          </button>
        </div>
      </div>

      <!-- Voucher / summary stays as-is -->
      <BookingVoucher :bookingNumber="bookingNumber" />
    </div>

    <!-- ERROR FEED (payment but not approved) -->
    <div v-else-if="feedType === 'error'" class="col-span-12 md:pb-12 flex flex-col items-center justify-center gap-6">
      <div class="relative">
        <div class="flex items-center justify-center absolute -left-3 w-[100px] h-[100px] rounded-full border border-theme-gray-7">
          <font-awesome-icon size="2xl" icon="fa-solid fa-check" class="text-theme-active" />
        </div>
        <div class="bg-theme-gray-1 w-[100px] h-[100px] rounded-full"></div>
      </div>
      <h3 class="text-theme-gray-5 text-head-md-2 md:text-head-xl text-center flex flex-col gap-3">
        <div>Thank You</div>
      </h3>
      <div class="text-center flex flex-col gap-1 md:w-[80%]">
        <div class="rounded-md bg-yellow-50 p-4">
          <div class="flex flex-col items-start gap-2 md:flex-row md:items-center">
            <div class="flex items-start gap-2">
              <font-awesome-icon size="lg" icon="fa-solid fa-circle-exclamation" class="text-yellow-400" />
              <div class="flex flex-col md:flex-row items-start gap-1">
                <p class="text-sm font-medium text-yellow-700">
                  A booking has been created for you but we did not receive payment from your bank. You will be contacted by our representative for further assistance
                </p>
                <div
                  @click="handleGoBack"
                  class="font-medium text-sm text-yellow-700 underline hover:text-yellow-600 cursor-pointer"
                >
                  {{ isAgencyOrCorporate ? 'Go to dashboard' : 'Go to home' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Loader :show="isLoading" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import LoaderIcon from '@/components/LoaderIcon.vue'
import {
  handleApiError,
  clearSessionData,
  getBookingNumber,
  getPaymentSession,
  getPaymentTransaction,
  getPaymentCode,
  isLoggedIn,
  getDealId,
  getPaymentWallet,
} from '@/utils/common'
import Payment from '@/composables/Payment'
import BookingService from '@/services/booking-service'
import { useRouter, useRoute } from 'vue-router'
import CarService from '@/services/car-service'
import Loader from '@/widgets/Loader.vue'
import BookingVoucher from '@/pages/BookingVoucher.vue'
import Deal from '@/composables/Deal'

const props = defineProps(['currentStep'])

const isLoading = ref<boolean>(true)
const hasError  = ref<boolean>(false)

const booking: string | null     = getBookingNumber()
const transaction: string | null = getPaymentTransaction()
const session: string | null     = getPaymentSession()
const cvc: string | null         = getPaymentCode()
const paymentWallet: string | null = getPaymentWallet()

const isPaying = computed(() => {
  return !!(session && transaction)
})

const { authorizePayment, verifyPaymentStatus } = Payment()
const payload    = ref<any>(null)
const responsee  = ref<any>(null)
const { finalizeeBooking, readBooking } = BookingService()

const feed     = ref<any>()
const feedType = ref<'success' | 'error'>()

const router = useRouter()
const route = useRoute()
const { retrieveUserSelectedCar } = CarService()

const bookingNumber = ref<string>()
const dealId        = ref<string>()

const message      = ref<string>('Booking has been complete and payment has been recieved')
const errorMessage = ref<string>('')

const authUser = isLoggedIn()
const isAgencyOrCorporate = authUser && (authUser.user.type === 'agent' || authUser.user.type === 'corporate')

const handleGoBack = () => {
  if (isAgencyOrCorporate) {
    router.push({ name: 'AccountDashboard' })
  } else {
    router.push({ name: 'Home' })
  }
}

const { createDeal, updateDeal, createContact } = Deal()

onMounted(async () => {
  bookingNumber.value = getBookingNumber() as string
  dealId.value        = getDealId() as string

  if (!bookingNumber.value) {
    router.push({ name: 'Home' })
    return
  }

  const carResponse: any = retrieveUserSelectedCar()
  const isSuccessHint = route.query.status === 'success'

  // Fetch booking details to verify if it is a quote request
  const currentBooking = await readBooking(bookingNumber.value as string)
  console.log('--- DEBUG FINISH.VUE ---')
  console.log('bookingNumber:', bookingNumber.value)
  console.log('currentBooking success:', currentBooking.success)
  console.log('currentBooking data structure:', currentBooking.data)
  const isQuoteRequest = currentBooking.success && currentBooking.data?.data && 
    (currentBooking.data.data.status === 'need_confirmation' || currentBooking.data.data.status === 'incomplete' || currentBooking.data.data.status === 'unconfirmed')
  console.log('isQuoteRequest evaluated:', isQuoteRequest)
  console.log('------------------------')

  // 1. “Lead” / Quote Request flow (no price or need confirmation status from database)
  if ((carResponse && (carResponse.price == null || carResponse.price === 0)) || isQuoteRequest) {
    feedType.value = 'success'
    const finalResponse = await finalizeeBooking(bookingNumber.value, feedType.value, {}, true)
    isLoading.value = false

    if (finalResponse.success) {
      await upsertDeal()
      message.value = 'Your request has been created. You will be contacted by our representative shortly.'
      clearSessionData()
    } else {
      hasError.value = true
    }
    return
  }

  // 2. Active Server-to-Server Verification (Poll for payment status)
  let attempts = 0
  const maxAttempts = 4 // 4 attempts * 4 seconds = 16 seconds
  
  if (isSuccessHint) {
     message.value = 'Payment received! We are just finalizing your booking, please wait...'
  }

  const checkPaymentStatus = async () : Promise<boolean> => {
    // Actively ask Credimax for the real-time status
    const verifyResp = await verifyPaymentStatus(bookingNumber.value as string)
    
    if (verifyResp && verifyResp.data && verifyResp.data.payment_status === 'paid') {
      feedType.value = 'success'
      isLoading.value = false
      message.value = 'Thank you for booking with Vehitra World! Your voucher and receipt will be sent to your email shortly.'
      await upsertDeal()
      clearSessionData()
      return true
    } else if (verifyResp && verifyResp.data && verifyResp.data.payment_status === 'failed') {
      // Only show error if we DON'T have a success hint from the URL
      if (!isSuccessHint) {
        feedType.value = 'error'
        isLoading.value = false
        hasError.value = true
        errorMessage.value = 'Payment Failed. The transaction was declined or your card is not supported.'
        return true
      }
    }

    // Fallback: check local database
    const currentBooking = await readBooking(bookingNumber.value as string)
    if (currentBooking.success && currentBooking.data?.data && (currentBooking.data.data.payment?.status === 'paid' || currentBooking.data.data.status === 'confirmed')) {
      feedType.value = 'success'
      isLoading.value = false
      message.value = 'Thank you for booking with Vehitra World! Your voucher and receipt will be sent to your email shortly.'
      await upsertDeal()
      clearSessionData()
      return true
    }
    
    return false // Keep polling
  }

  // Initial check
  if (await checkPaymentStatus()) return

  // Polling loop
  const pollInterval = setInterval(async () => {
    attempts++
    if (await checkPaymentStatus()) {
      clearInterval(pollInterval)
    } else if (attempts >= maxAttempts) {
      clearInterval(pollInterval)
      
      // 16 SECONDS PASSED - Show success message but keep the current (pending) voucher
      feedType.value = 'success'
      isLoading.value = false
      message.value = 'Thank you for booking with Vehitra World! Your voucher and receipt will be sent to your email shortly.'
      
      // We don't refresh the booking data here because we WANT it to show as "Request" 
      // until the bank actually confirms it later via webhook.
      clearSessionData()
    }
  }, 4000)

  const finishFinalCapture = async () => {
    if (paymentWallet === 'card' && transaction) {
      const response = await authorizePayment(session, booking, transaction, cvc as string)
      if (response.status === 200) {
        const res = response.data.response

        if (!res || !res.response || res.response.gatewayCode !== 'APPROVED' || !res.result || res.result !== 'SUCCESS') {
          feed.value = res
          feedType.value = 'error'
        } else {
          feed.value = res.order
          feedType.value = 'success'
        }

        const finalResponse = await finalizeeBooking(bookingNumber.value as string, feedType.value as any, feed.value, false)
        isLoading.value = false

        await upsertDeal()

        if (finalResponse.success) {
          message.value = 'A booking has been created and payment has been received'
          clearSessionData()
        } else {
          hasError.value = true
          errorMessage.value = 'We encountered an error while processing your payment'
        }
      } else {
        isLoading.value = false
        hasError.value = true
        errorMessage.value = handleApiError(response)
      }
    } else if (paymentWallet === 'apple_pay') {
      feedType.value = 'success'
      feed.value = 'apple_pay'
      const finalResponse = await finalizeeBooking(bookingNumber.value as string, feedType.value as any, feed.value, false)
      isLoading.value = false

      if (finalResponse.success) {
        message.value = 'A booking has been created and payment has been received'
        clearSessionData()
      } else {
        hasError.value = true
        errorMessage.value = 'We encountered an error while processing your payment'
      }
    } else {
      isLoading.value = false
      hasError.value = true
      errorMessage.value = 'Payment confirmation is pending. Please check your email or contact support.'
    }
  }
})

const upsertDeal = async () => {
  const bookingId = parseInt(bookingNumber.value as string)
  const deal      = parseInt(dealId.value as string)

  if (authUser && (authUser.user.type === 'agent' || authUser.user.type === 'corporate')) {
    await updateDeal(deal, bookingId)
  } else {
    await createContact(bookingId)
    await createDeal(bookingId)
  }
}
</script>